import { useEffect, useState } from 'react'
import { AlertCircle, ExternalLink, FileText, Loader2, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { isPreviewableImage } from '@/lib/equipmentDocuments'
import { equipmentDocumentsApi } from '@/api/equipmentDocumentsApi'

/**
 * The expanded body of one document row (ClickUp z8nrz7cnj9).
 *
 * Mounted only when a row is expanded, and that is the point: it fetches its
 * own presigned download URL, so the cost is one request per preview a user
 * actually opens rather than one per document listed. The renderer's
 * AttachmentThumb fetches eagerly for every attachment on the page — the same
 * shape of request burst that earned a clinician a 429 on the case page
 * (z8nrz7cnhz), which is why this one waits to be asked.
 *
 * The URL is deliberately NOT held in a query cache: it is short-lived and
 * single-use, so a cached one would hand back a dead link.
 */
export function EquipmentDocumentPreview({
  documentId,
  fileName,
  mimeType,
}: {
  documentId: string
  fileName: string
  mimeType: string
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Bumped by the retry button, so a click re-runs the effect without needing a
  // separate imperative fetch path.
  const [retryTick, setRetryTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    equipmentDocumentsApi
      .getDownloadUrl(documentId)
      .then(res => {
        if (!cancelled) setUrl(res.url)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
    // `documentId` is enough. Unlike a form attachment — where a replace keeps
    // the same (submissionId, fieldId) and only storageKey moves — a document's
    // file never changes: rename touches the name, and a new file is a new row.
  }, [documentId, retryTick])

  if (error) {
    return (
      <div className='flex items-center justify-between gap-2 rounded-md border border-dashed border-destructive/40 bg-destructive/5 px-3 py-2'>
        <p className='flex min-w-0 items-center gap-1.5 text-[11px] text-destructive'>
          <AlertCircle className='h-3.5 w-3.5 shrink-0' />
          <span className='truncate'>{error}</span>
        </p>
        <Button
          type='button'
          size='sm'
          variant='ghost'
          className='h-7 shrink-0 gap-1 text-xs'
          onClick={() => setRetryTick(t => t + 1)}
        >
          <RotateCw className='h-3.5 w-3.5' />
          Retry
        </Button>
      </div>
    )
  }

  if (!url) {
    return (
      <div className='flex h-24 items-center justify-center rounded-md border bg-muted/30'>
        <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
      </div>
    )
  }

  return (
    <div className='space-y-2'>
      {isPreviewableImage(mimeType) ? (
        <img
          src={url}
          alt={fileName}
          className='max-h-64 w-full rounded-md border bg-muted/20 object-contain'
        />
      ) : (
        /*
         * PDFs get an honest placeholder, not an <iframe>. A presigned URL in a
         * frame is CSP-blocked on some deployments and renders empty, which
         * looks like a broken feature rather than an unsupported one.
         */
        <div className='flex h-24 flex-col items-center justify-center gap-1 rounded-md border bg-muted/30 text-muted-foreground'>
          <FileText className='h-5 w-5 opacity-40' />
          <span className='text-[11px]'>No inline preview for this type</span>
        </div>
      )}

      <Button
        type='button'
        size='sm'
        variant='outline'
        className='h-7 w-full gap-1.5 text-xs'
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
      >
        <ExternalLink className='h-3.5 w-3.5' />
        Open in a new tab
      </Button>
    </div>
  )
}
